import React from "react";
import LegalLayout from "./LegalLayout";

export default function Privacy() {
    return (
        <LegalLayout title="Privacy Policy" lastUpdated="February 17, 2026" path="/privacy">
            <p>
                This Privacy Policy explains what data KickLoom collects, why, and
                how we protect it. We try to be honest about this and to collect
                the minimum we need to run the Service.
            </p>

            <h2>1. Data we collect</h2>
            <ul>
                <li>
                    <strong>Account data</strong> — name, email, hashed password,
                    subscription tier. Collected when you register or upgrade.
                </li>
                <li>
                    <strong>Payment metadata</strong> — Paystack transaction
                    references and status. We <em>never</em> see your card
                    number or bank details — those live with Paystack only.
                </li>
                <li>
                    <strong>Usage data</strong> — request logs (IP, user-agent,
                    path, timestamp) kept for up to 30 days for security and
                    debugging.
                </li>
                <li>
                    <strong>Prediction inputs you submit</strong> — fixtures you
                    paste into the Quick AI Predictions form (VIP). Stored only
                    long enough to return the response, not retained beyond that.
                </li>
            </ul>

            <h2>2. How we use it</h2>
            <ul>
                <li>To authenticate you and provide the Service.</li>
                <li>To process VIP payments via Paystack.</li>
                <li>To send transactional emails (verification, password reset, billing confirmations).</li>
                <li>To detect abuse and prevent fraud.</li>
                <li>To generate aggregated, non-personal usage statistics.</li>
            </ul>

            <h2>3. Who we share it with</h2>
            <p>We share only what is strictly necessary, with these processors:</p>
            <ul>
                <li><strong>Paystack</strong> — payment processing (PCI-DSS certified).</li>
                <li><strong>OpenAI</strong> — text generation for AI predictions. Submitted fixtures may pass through OpenAI's API for inference. We don't send personally identifying information in those prompts.</li>
                <li><strong>API-Football</strong> — sports data lookups. No personal data is sent.</li>
                <li><strong>Our hosting provider</strong> — for application + database storage.</li>
            </ul>
            <p>
                We do <strong>not</strong> sell your data to advertisers and we do
                not run third-party ad networks on KickLoom.
            </p>

            <h2>4. Security</h2>
            <p>
                Passwords are stored using BCrypt with a per-user salt and a high
                work factor. Sessions use signed JWT tokens. The site is served
                over HTTPS. We rotate secrets and patch dependencies on a regular
                schedule.
            </p>

            <h2>5. Retention</h2>
            <ul>
                <li>Account data: kept while your account is active, deleted within 30 days of account closure.</li>
                <li>Billing records: kept for 7 years for tax/accounting purposes (as required by law).</li>
                <li>Request logs: 30 days.</li>
            </ul>

            <h2>6. Your rights</h2>
            <p>
                Depending on where you live, you have the right to: access your
                data, correct it, export it, ask us to delete it, and object to
                certain uses. Email{" "}
                <a className="text-primary underline" href="mailto:privacy@kickloom.com">
                    privacy@kickloom.com
                </a>{" "}
                with your request — we respond within 30 days.
            </p>

            <h2>7. Cookies</h2>
            <p>
                KickLoom uses a single first-party token stored in
                <code className="mx-1 font-mono text-primary">localStorage</code>
                to keep you logged in. We do not use third-party tracking cookies
                or analytics scripts that profile you across sites.
            </p>

            <h2>8. Children</h2>
            <p>
                KickLoom is not directed at children under 18. We do not knowingly
                collect data from anyone under 18.
            </p>

            <h2>9. Changes</h2>
            <p>
                If we make material changes to this policy, we'll post them here
                and, where appropriate, email you.
            </p>

            <h2>10. Contact</h2>
            <p>
                Email{" "}
                <a className="text-primary underline" href="mailto:privacy@kickloom.com">
                    privacy@kickloom.com
                </a>{" "}
                for any privacy questions or data-subject requests.
            </p>
        </LegalLayout>
    );
}
